import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, Grid, Typography, Stack, IconButton, Menu, MenuItem, Paper, TextField, InputAdornment } from "@mui/material";
import TableComponent from "components/grid";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { FaPlus } from "react-icons/fa6";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomButton from "components/Button";
import moment from "moment";
import { DEFAULT_DATE_FORMAT } from "config";
import CustomPagination from "components/CustomPagination";
import AddDependency from "./AddDependency";
import { useLazyGetAllDependenciesQuery } from "store/slices/apis/customerDependenciesSlice";
import EditDependency from "./EditDependency";
import SearchIcon from "@mui/icons-material/Search";


const CustomerDependenciesList = () => {

    const [isAddDependency, setIsAddDependency] = useState(false)
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [searchText, setSearchText] = useState("");

    // sorting asc desc
    const [sortOrder, setSortOrder] = React.useState('desc');
    const [sortBy, setSortBy] = React.useState('createdDate');

    // page size
    const [pageNumber, setPageNumber] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);

    const [filters, setFilters] = useState({
        // ProjectId: projectId,
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
    });

    const [isEditDependency, setIsEditDependency] = useState(false);
    const [rowData, setRowData] = useState(false);
    // const workflowList = useLazyGetAllWorkflowConfigrationQuery(
    //     {
    //         ProjectId: projectId,
    //         SortBy: sortBy,
    //         SortOrder: sortOrder,
    //         PageNumber: pageNumber,
    //         PageSize: pageSize,
    //     }
    // )
    const [trigger, { data: workflowList, isFetching }] = useLazyGetAllDependenciesQuery();
    const filteredData = useMemo(() => {
        const rows = workflowList?.data ?? [];
        const term = searchText.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((row) =>
            [
                row?.dependencyOnSafeExpress,
                row?.status,
                row?.remarks,
                row?.dueDate,
                row?.targetDate,
                row?.receivedDate,
            ]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(term))
        );
    }, [workflowList?.data, searchText]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };


    useEffect(() => {
        trigger(filters);
    }, [filters, trigger]);

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'

        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);

    };

    //list action menu
    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };
    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditWorkflow(menuRow);
        }
        handleMenuClose();
    };

    const handleEditWorkflow = (row) => {
        setIsEditDependency((prev) => {
            const newState = !prev;
            setRowData(newState ? row : null);
            return newState;
        });
    }

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, row)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
        {
            name: "Dependency On Safexpress",
            field: "dependencyOnSafeExpress",
            width: "260px",
            renderer: (row) => (
                <Box>
                    <Typography sx={{
                        fontSize: "13px", maxWidth: "190px", fontWeight: 700, fontFamily: "Outfit", color: "#433F5E", overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}>
                        {row?.dependencyOnSafeExpress}
                    </Typography>
                </Box>
            ),
        },
        {
            name: 'Status',
            field: 'status',
            renderer: (row) => (
                <Stack direction="row" spacing={1}>
                    <Chip size="small" sx={{
                        fontSize: '12px',
                        '& .MuiChip-label': {
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            fontWeight: 600
                        }
                    }} label={`${row?.status}`} />
                </Stack>
            )
        },
        {
            name: "Due Date",
            field: "dueDate",
            renderer: (row) => (
                <>
                    {row?.dueDate ?
                        moment(row?.dueDate).format(DEFAULT_DATE_FORMAT)
                        : ''
                    }
                </>
            ),
            // width: "7%",
        },
        {
            name: "Target Date",
            field: "targetDate",
            renderer: (row) => (
                <>
                    {row?.targetDate ?
                        moment(row?.targetDate).format(DEFAULT_DATE_FORMAT)
                        : ''
                    }
                </>
            ),
            // width: "7%",
        },
        {
            name: "Received Date",
            field: "receivedDate",
            renderer: (row) => (
                <>
                    {row?.receivedDate ?
                        moment(row?.receivedDate).format(DEFAULT_DATE_FORMAT)
                        : ''
                    }
                </>
            ),
            // width: "7%",
        },
        {
            name: "Remarks",
            field: "remarks",
            // width: "7%",
        },
        {
            name: "Created at",
            field: "createdDate",
            // width: "10%",
            renderer: (row) => (
                <>
                    {row?.createdDate ?
                        moment(row?.createdDate).format(DEFAULT_DATE_FORMAT)
                        : ''
                    }
                </>
            ),
        },
        {
            name: "Last Updated at",
            field: "updateDate",
            // width: "10%",
            renderer: (row) => (
                <>
                    {row?.updateDate ?
                        moment(row?.updateDate).format(DEFAULT_DATE_FORMAT)
                        : ''
                    }
                </>
                // moment(row?.updateDate).format(DEFAULT_DATE_FORMAT)
            ),
        },
    ];

    const handleAddDependency = () => {
        setIsAddDependency(!isAddDependency);
    }
    return (
        <>
            <Box sx={{ px: 2, py: 1.5, bgcolor: "#FFFFFF", borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 650, fontSize: "20px", lineHeight: "27px", color: "#0F172A", fontFamily: "Outfit, sans-serif" }}>Customer Dependencies</Typography>
                    <Typography sx={{ mt: 0.2, fontSize: "12px", lineHeight: "17px", color: "#64748B" }}>Track customer inputs, commitments, target dates, and delivery dependencies.</Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <TextField
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Search dependencies"
                        size="small"
                        sx={{
                            minWidth: { xs: "100%", sm: 280 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "3px",
                                bgcolor: "#FFFFFF",
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <CustomButton label="Add Dependency" startIcon={<FaPlus />} onClick={handleAddDependency} sx={{ minHeight: 36, px: 2, py: 0.8, fontSize: 13, fontWeight: 600, flexShrink: 0 }} />
                </Stack>
            </Box>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={12}>
                    <Paper sx={{ borderRadius: "3px", border: "1px solid #E2E8F0", overflow: "hidden" }}>
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    mt: 1,
                                    borderRadius: "3px",
                                    minWidth: 140,
                                    border: "1px solid #E5E9EB",
                                    boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                    overflow: "hidden",
                                },
                            }}
                            MenuListProps={{ sx: { py: 0.5 } }}
                        >
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": { backgroundColor: "#F6F8F9" },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        </Menu>
                        <TableComponent
                            columns={columns}
                            data={filteredData}
                            createSortHandler={handleRequestSort}
                            loading={isFetching}
                            order={sortOrder}
                            orderBy={sortBy}
                            containerSx={{ maxHeight: 580 }}
                        />
                        <CustomPagination
                            totalCount={workflowList?.totalRecords || 0}
                            pageSize={pageSize}
                            page={pageNumber}
                            onPageChange={handlePageChange}
                            setCurrentPage={setPageNumber}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </Paper>
                </Grid>
                {isAddDependency &&
                    <AddDependency open={isAddDependency} handleClose={handleAddDependency} />
                }
                {
                    isEditDependency &&
                    <EditDependency open={isEditDependency} handleClose={handleEditWorkflow} rowData={rowData} />
                }
            </Grid>
        </>
    );
}

export default CustomerDependenciesList;
